/**
 * Date keys — the one place the app decides what "today" is.
 *
 * Kobo is a day-bucketed app: tasks, workout days and stats are all addressed
 * by a `YYYY-MM-DD` key. Getting that key wrong by one day is the worst class of
 * bug this app can have — it silently breaks a streak the user has kept for
 * months — so every conversion lives here and nowhere else.
 *
 * Two rules, both learned from the Swift app and from PocketBase's storage:
 *
 * 1. **A date key is a LOCAL calendar day.** `new Date().toISOString()` is UTC
 *    and is wrong for anyone west of Greenwich after 17:00 (and east of it
 *    before 08:00). Never derive a key from an ISO string; use `toDateKey`.
 *
 * 2. **PocketBase stores those days as midnight UTC** (`2026-08-04
 *    00:00:00.000Z`), which is a storage convention, not a moment in time.
 *    Converting it back with `new Date(value)` re-applies the local offset and
 *    shifts the day. Use `dateKeyFromPbDate`, which reads the literal date part.
 *
 * Everything here is a pure function of its arguments (the clock is always an
 * injectable `now`), so it is directly unit-testable.
 */

/** A local calendar day in `YYYY-MM-DD` form. */
export type DateKey = string;

export const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The hour at which the logical day rolls over, 0–23.
 *
 * Kobo rolls at midnight. The Swift app made this configurable and shipped a
 * default of 05:00 so a 2 a.m. session still counted toward the previous day;
 * the option is preserved on every function here as `dayStartHour` so that
 * setting can come back without a rewrite, but nothing sets it today.
 */
export const DEFAULT_DAY_START_HOUR = 0;

export type DayBoundaryOptions = {
  /** Overrides {@link DEFAULT_DAY_START_HOUR}. Clamped to 0–23. */
  dayStartHour?: number;
};

const MS_PER_DAY = 86_400_000;

/* -------------------------------------------------------------------------- */
/* Parsing and formatting                                                      */
/* -------------------------------------------------------------------------- */

/** True when the value is a syntactically valid, real calendar day. */
export function isDateKey(value: unknown): value is DateKey {
  if (typeof value !== "string" || !DATE_KEY_PATTERN.test(value)) {
    return false;
  }
  const [year, month, day] = splitKey(value);
  if (month < 1 || month > 12 || day < 1) {
    return false;
  }
  return day <= daysInMonth(year, month);
}

/** The local calendar day the instant falls on. */
export function toDateKey(date: Date): DateKey {
  return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1, 2)}-${pad(date.getDate(), 2)}`;
}

/**
 * The key as a local `Date`, at **noon**.
 *
 * Noon rather than midnight on purpose: in the handful of zones that start DST
 * at 00:00 (São Paulo, Santiago, Havana) local midnight does not exist on that
 * date and the runtime silently returns 01:00 of the following day. Noon is
 * never skipped, so day arithmetic built on it cannot drift.
 */
export function fromDateKey(key: DateKey): Date {
  const parsed = tryFromDateKey(key);
  if (!parsed) {
    throw new RangeError(`Not a date key: ${JSON.stringify(key)}`);
  }
  return parsed;
}

/** {@link fromDateKey} that returns null instead of throwing. */
export function tryFromDateKey(key: string): Date | null {
  if (!isDateKey(key)) {
    return null;
  }
  const [year, month, day] = splitKey(key);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

/** Local midnight of the day the instant falls on. */
export function startOfLocalDay(date: Date): Date {
  const start = new Date(date.getTime());
  start.setHours(0, 0, 0, 0);
  return start;
}

/* -------------------------------------------------------------------------- */
/* Today, and the rollover                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The key of the current logical day.
 *
 * With the default midnight boundary this is simply the local calendar day. If
 * a later day-start hour is configured, the hours before it still belong to the
 * previous key — computed by calendar arithmetic rather than by subtracting
 * milliseconds, so a DST transition cannot push it off by a day.
 */
export function todayKey(now: Date = new Date(), options: DayBoundaryOptions = {}): DateKey {
  const boundary = clampHour(options.dayStartHour ?? DEFAULT_DAY_START_HOUR);
  const day = startOfLocalDay(now);
  if (now.getHours() < boundary) {
    day.setDate(day.getDate() - 1);
  }
  return toDateKey(day);
}

export function yesterdayKey(now: Date = new Date(), options: DayBoundaryOptions = {}): DateKey {
  return addDays(todayKey(now, options), -1);
}

export function tomorrowKey(now: Date = new Date(), options: DayBoundaryOptions = {}): DateKey {
  return addDays(todayKey(now, options), 1);
}

/**
 * Milliseconds from `now` until the day key changes.
 *
 * A timer scheduled with this is what makes the app roll over while it is left
 * open overnight. The result is always at least one second so a caller cannot
 * spin, and is recomputed from the real clock each time rather than assuming a
 * day is 24 hours — it is 23 or 25 on DST boundaries.
 */
export function msUntilNextDayKey(
  now: Date = new Date(),
  options: DayBoundaryOptions = {},
): number {
  const boundary = clampHour(options.dayStartHour ?? DEFAULT_DAY_START_HOUR);
  const next = startOfLocalDay(now);
  next.setHours(boundary, 0, 0, 0);
  if (next.getTime() <= now.getTime()) {
    next.setDate(next.getDate() + 1);
    // Re-assert the hour: crossing a DST boundary can shift it by an hour.
    next.setHours(boundary, 0, 0, 0);
  }
  return Math.max(1_000, next.getTime() - now.getTime());
}

/* -------------------------------------------------------------------------- */
/* Arithmetic                                                                  */
/* -------------------------------------------------------------------------- */

/** The key `amount` days after this one (negative goes back). */
export function addDays(key: DateKey, amount: number): DateKey {
  const date = fromDateKey(key);
  date.setDate(date.getDate() + Math.trunc(amount));
  return toDateKey(date);
}

/** The first calendar day of the month after `key` belongs to. */
export function firstDayOfNextMonth(key: DateKey): DateKey {
  const date = fromDateKey(key);
  date.setMonth(date.getMonth() + 1, 1);
  return toDateKey(date);
}

/** A new `Date`, `amount` days from the given one, keeping its time of day. */
export function shiftDate(date: Date, amount: number): Date {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + Math.trunc(amount));
  return next;
}

/**
 * Whole days from `from` to `to` — positive when `to` is later.
 *
 * Computed on the UTC projection of the two calendar days so that a DST
 * transition in between cannot round a 23-hour gap down to zero.
 */
export function daysBetween(from: DateKey, to: DateKey): number {
  return Math.round((utcTimestamp(to) - utcTimestamp(from)) / MS_PER_DAY);
}

/** Sort comparator; date keys also happen to sort correctly as plain strings. */
export function compareDateKeys(a: DateKey, b: DateKey): -1 | 0 | 1 {
  return a === b ? 0 : a < b ? -1 : 1;
}

export function isBeforeDateKey(a: DateKey, b: DateKey): boolean {
  return a < b;
}

export function isAfterDateKey(a: DateKey, b: DateKey): boolean {
  return a > b;
}

/** Every key from `from` to `to` inclusive. Returns `[]` if the range is empty. */
export function dateKeyRange(from: DateKey, to: DateKey): DateKey[] {
  const span = daysBetween(from, to);
  if (span < 0) {
    return [];
  }
  const keys: DateKey[] = [];
  for (let offset = 0; offset <= span; offset += 1) {
    keys.push(addDays(from, offset));
  }
  return keys;
}

/** The `YYYY-MM` bucket a key belongs to. */
export function monthKey(key: DateKey): string {
  fromDateKey(key); // validates
  return key.slice(0, 7);
}

/** 0 = Sunday … 6 = Saturday. */
export function weekdayIndex(key: DateKey): number {
  return fromDateKey(key).getDay();
}

/** Saturday or Sunday. */
export function isWeekend(key: DateKey): boolean {
  const day = weekdayIndex(key);
  return day === 0 || day === 6;
}

/* -------------------------------------------------------------------------- */
/* Display                                                                     */
/* -------------------------------------------------------------------------- */

export type DateFormatStyle =
  /** `Mon 4 Aug` — the default for a day header. */
  | "weekday-short"
  /** `Monday, 4 August 2026` */
  | "full"
  /** `4 Aug 2026` */
  | "medium"
  /** `4 Aug` */
  | "short"
  /** `Monday` */
  | "weekday"
  /** `August 2026` */
  | "month"
  /** `4` */
  | "day";

const FORMAT_OPTIONS: Record<DateFormatStyle, Intl.DateTimeFormatOptions> = {
  "weekday-short": { weekday: "short", day: "numeric", month: "short" },
  full: { weekday: "long", day: "numeric", month: "long", year: "numeric" },
  medium: { day: "numeric", month: "short", year: "numeric" },
  short: { day: "numeric", month: "short" },
  weekday: { weekday: "long" },
  month: { month: "long", year: "numeric" },
  day: { day: "numeric" },
};

/**
 * Render a key for a human. `locale` is injectable so a test can pin the output;
 * left undefined it follows the user's locale, which is what the UI wants.
 */
export function formatDateKey(
  key: DateKey,
  style: DateFormatStyle = "weekday-short",
  locale?: string,
): string {
  return new Intl.DateTimeFormat(locale, FORMAT_OPTIONS[style]).format(fromDateKey(key));
}

/**
 * `Today` / `Yesterday` / `Tomorrow` where that is true, and a formatted date
 * everywhere else. Anchored on an explicit `today` so the label cannot disagree
 * with the data the screen is showing.
 */
export function relativeDayLabel(
  key: DateKey,
  today: DateKey,
  style: DateFormatStyle = "weekday-short",
  locale?: string,
): string {
  switch (daysBetween(today, key)) {
    case 0:
      return "Today";
    case -1:
      return "Yesterday";
    case 1:
      return "Tomorrow";
    default:
      return formatDateKey(key, style, locale);
  }
}

/** `3 days ago`, `in 2 days`, `today` — for due dates and carry-over notices. */
export function relativeDayCount(key: DateKey, today: DateKey): string {
  const delta = daysBetween(today, key);
  if (delta === 0) return "today";
  if (delta === -1) return "yesterday";
  if (delta === 1) return "tomorrow";
  if (delta < 0) return `${-delta} days ago`;
  return `in ${delta} days`;
}

/* -------------------------------------------------------------------------- */
/* PocketBase interop                                                          */
/* -------------------------------------------------------------------------- */

/**
 * A date key as PocketBase stores it: midnight UTC.
 *
 * `tasks.date`, `cards.due_date` and friends are day buckets, not instants, so
 * every writer must normalise to this exact shape or `date = "..."` filters
 * start missing rows.
 */
export function toPbDate(key: DateKey): string {
  fromDateKey(key); // validates
  return `${key} 00:00:00.000Z`;
}

/**
 * Read a PocketBase date field back into a key.
 *
 * Deliberately a string slice rather than a `Date` round-trip: the stored value
 * is a calendar day dressed up as a UTC instant, and parsing it re-applies the
 * viewer's offset, which moves the day for most of the planet.
 */
export function dateKeyFromPbDate(value: string | null | undefined): DateKey | null {
  if (!value) {
    return null;
  }
  const candidate = value.slice(0, 10);
  return isDateKey(candidate) ? candidate : null;
}

/**
 * An instant in the `YYYY-MM-DD HH:mm:ss.SSSZ` form PocketBase stores.
 *
 * Unlike a date key this really is a moment in time (`done_at`, `completed_at`),
 * so UTC is correct here and the conversion is a plain ISO reformat.
 */
export function toPbDateTime(instant: Date = new Date()): string {
  return instant.toISOString().replace("T", " ");
}

/* -------------------------------------------------------------------------- */

function splitKey(key: string): [number, number, number] {
  return [Number(key.slice(0, 4)), Number(key.slice(5, 7)), Number(key.slice(8, 10))];
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function utcTimestamp(key: DateKey): number {
  const [year, month, day] = splitKey(key);
  if (!isDateKey(key)) {
    throw new RangeError(`Not a date key: ${JSON.stringify(key)}`);
  }
  return Date.UTC(year, month - 1, day);
}

function clampHour(hour: number): number {
  if (!Number.isFinite(hour)) {
    return DEFAULT_DAY_START_HOUR;
  }
  return Math.min(23, Math.max(0, Math.trunc(hour)));
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, "0");
}
